import React, { useEffect, useState, useContext } from "react";
import {
  Box, Button, FormControl, FormLabel, Input, Select, Stack, HStack, Text, Heading, Table, Thead, Tbody, Tr, Th, Td, useToast, Spinner
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useAuth } from "../../hooks/useAuth";
import AuthContext from '../../contexts/AuthContext';
import { getCurrencySymbol } from "../../utility/formatCurrency";

const Trading212Page = () => {
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [assets, setAssets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [freeBalance, setFreeBalance] = useState(null);
  const [accountValue, setAccountValue] = useState(null);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");
  const [newKeySecret, setNewKeySecret] = useState("");
  const [orderTicker, setOrderTicker] = useState("");
  const [orderQuantity, setOrderQuantity] = useState("");
  const [orderLimitPrice, setOrderLimitPrice] = useState("");
  const toast = useToast();
  const {getToken} = useContext(AuthContext);

  const token = getToken();

  // Helper per chiamate API con autenticazione
  const api = axios.create({
    baseURL: "/api/trading212",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // Carica le chiavi API all'avvio
  useEffect(() => {
    api.get("/keys")
      .then(res => setKeys(res.data))
      .catch(() => toast({ status: "error", title: "Errore nel caricamento chiavi" }));
    // eslint-disable-next-line
  }, []);

  // Carica asset e ordini quando cambia la chiave selezionata
  useEffect(() => {
    if (!selectedKey) return;
    api.get(`/currency?key_id=${selectedKey}`)
      .then(res => {
        setCurrency(getCurrencySymbol(res.data.currency));
      })
      .catch(() => toast({ status: "error", title: "Errore nel caricamento valuta" }));
    setLoadingAssets(true);
    api.get(`/assets?key_id=${selectedKey}`)
      .then(res => setAssets(res.data))
      .catch(() => toast({ status: "error", title: "Errore nel caricamento asset" }))
      .finally(() => setLoadingAssets(false));
    setLoadingOrders(true);
    api.get(`/orders?key_id=${selectedKey}`)
      .then(res => setOrders(res.data))
      .catch(() => toast({ status: "error", title: "Errore nel caricamento ordini" }))
      .finally(() => setLoadingOrders(false));
    setLoadingBalance(true);
    api.get(`/balance?key_id=${selectedKey}`)
      .then(res => {
        setAccountValue(res.data.total);
        setFreeBalance(res.data.free);
      })
      .catch(() => toast({ status: "error", title: "Errore nel caricamento bilancio" }))
      .finally(() => setLoadingBalance(false));
      // eslint-disable-next-line
  }, [selectedKey]);

  // Aggiungi nuova chiave
  const handleAddKey = (e) => {
    e.preventDefault();
    api.post("/keys", { name: newKeyName, api_key: newKeyValue, api_secret: newKeySecret })
      .then(res => {
        setKeys([...keys, { id: res.data.id, name: newKeyName, created_at: new Date().toISOString() }]);
        setNewKeyName("");
        setNewKeyValue("");
        toast({ status: "success", title: "Chiave aggiunta" });
      })
      .catch(() => toast({ status: "error", title: "Errore nel salvataggio chiave" }));
  };

  const handleDeleteKey = () => {
    if (!selectedKey) return;

    api.delete(`/keys/${selectedKey}`)
      .then(() => {
        setKeys(keys.filter(k => k.id !== selectedKey));
        setSelectedKey(null);
        toast({ status: "success", title: "Chiave eliminata" });
      })
      .catch(() => toast({ status: "error", title: "Errore nell'eliminazione chiave" }));
  };

  // Piazzare ordine di mercato
  const handlePlaceOrder = (e) => {
    e.preventDefault();
    if (!selectedKey) return;

    let payload = {
      key_id: selectedKey,
      ticker: orderTicker,
      quantity: parseFloat(orderQuantity)
    };

    if (orderLimitPrice != ""){
      payload = Object.assign(payload, { limitPrice: parseFloat(orderLimitPrice) });
    }
    api.post("/orders", payload)
      .then(() => {
        toast({ status: "success", title: "Ordine piazzato" });
        setOrderTicker("");
        setOrderQuantity("");
        setOrderLimitPrice("");
        // Ricarica ordini
        api.get(`/orders?key_id=${selectedKey}`).then(res => setOrders(res.data));
      })
      .catch(err => toast({ status: "error", title: err.response?.data?.error || "Errore ordine" }));
  };

  const handleDeleteOrder = (orderId) => {
    if (!selectedKey) return;
    api.delete(`/orders/delete/${orderId}`, { data: { key_id: selectedKey } })
      .then(() => {
        toast({ status: "success", title: "Ordine cancellato" });
        // Ricarica ordini
        api.get(`/orders?key_id=${selectedKey}`).then(res => setOrders(res.data));
      })
      .catch(err => toast({ status: "error", title: err.response?.data?.error || "Errore cancellazione ordine" }));
  };

  return (
    <Box mx="auto" p={6}>
      <Heading mb={6}>Trading 212 - Gestione API e Portafoglio</Heading>

      {/* Sezione aggiunta chiave */}
      <Box mb={8} p={4} borderWidth={1} borderRadius="md">
        <form onSubmit={handleAddKey}>
          <Stack direction={{ base: "column", md: "row" }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Nome chiave</FormLabel>
              <Input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Demo o Reale" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>API Key</FormLabel>
              <Input value={newKeyValue} onChange={e => setNewKeyValue(e.target.value)} placeholder="Copia qui la tua API Key" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>API Secret</FormLabel>
              <Input value={newKeySecret} onChange={e => setNewKeySecret(e.target.value)} placeholder="Copia qui il tuo API Secret" />
            </FormControl>
            <Button type="submit" colorScheme="blue" mt={{ base: 4, md: 8 }} minW="80px">Aggiungi</Button>
          </Stack>
        </form>
      </Box>

      {/* Selezione chiave */}
      <FormControl mb={6}>
        <FormLabel>Seleziona chiave API</FormLabel>
        <HStack spacing={4} align="end">
          <Select
            placeholder="Seleziona una chiave"
            value={selectedKey || ""}
            onChange={e => setSelectedKey(Number(e.target.value))}
            flex="1"
          >
            {keys.map(k => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </Select>

          <Button colorScheme="red" onClick={handleDeleteKey} isDisabled={!selectedKey} aria-label="Elimina chiave"><DeleteIcon /></Button>
        </HStack>
      </FormControl>

      {/* Bilancio */}
      <Box mb={8}>
        <Heading size="md" mb={2}>Bilancio</Heading>
        {loadingBalance ? <Spinner /> : (
          <Text>Valore Conto: {accountValue !== null ? `${accountValue} ${currency || ""}` : "-"} | Bilancio Disponibile: {freeBalance !== null ? `${freeBalance} ${currency || ""}` : "-"}</Text>
        )}
      </Box>

      {/* Asset */}
      <Box mb={8}>
        <Heading size="md" mb={2}>Asset</Heading>
        {loadingAssets ? <Spinner /> : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Ticker</Th>
                <Th>ISIN</Th>
                <Th>Quantità</Th>
                <Th>Prezzo Medio ({currency != null ? currency : ""})</Th>
                <Th>Prezzo Attuale ({currency != null ? currency : ""})</Th>
                <Th>P/L ({currency != null ? currency : ""})</Th>
              </Tr>
            </Thead>
            <Tbody>
              {assets.length === 0 && (
                <Tr>
                  <Td colSpan={5}><Text>Nessun asset trovato.</Text></Td>
                </Tr>
              )}
              {assets.map((a, idx) => (
                <Tr key={idx}>
                  <Td>{a.instrument.name}</Td>
                  <Td>{a.instrument.ticker}</Td>
                  <Td>{a.instrument.isin}</Td>
                  <Td>{a.quantity}</Td>
                  <Td>{a.averagePricePaid}</Td>
                  <Td>{a.currentPrice}</Td>
                  {a.walletImpact.unrealizedProfitLoss >= 0 ? (
                    <Td color="green.500">{a.walletImpact.unrealizedProfitLoss}</Td>
                  ) : (
                     <Td color="red.500">{a.walletImpact.unrealizedProfitLoss}</Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Ordini */}
      <Box mb={8}>
        <Heading size="md" mb={2}>Ordini</Heading>
        {loadingOrders ? <Spinner /> : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Ticker</Th>
                <Th>Quantità</Th>
                <Th>Status</Th>
                <Th>Limit Price ({currency != null ? currency : ""})</Th>
                <Th>Quantità Eseguita</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {orders.length === 0 && (
                <Tr>
                  <Td colSpan={5}><Text>Nessun ordine trovato.</Text></Td>
                </Tr>
              )}
              {orders.map((o, idx) => (
                <Tr key={idx}>
                  <Td>{o.ticker}</Td>
                  <Td>{o.quantity}</Td>
                  <Td>{o.status || "-"}</Td>
                  <Td>{o.limitPrice || "-"}</Td>
                  <Td>{o.filledQuantity || "-"}</Td>
                  <Td><Button size="xs" onClick={() => handleDeleteOrder(o.id)}>Cancella Ordine</Button></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Piazzare ordine */}
      <Box mb={8} p={4} borderWidth={1} borderRadius="md">
        <form onSubmit={handlePlaceOrder}>
          <Stack direction={{ base: "column", md: "row" }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Ticker</FormLabel>
              <Input value={orderTicker} onChange={e => setOrderTicker(e.target.value)} placeholder="AAPL_US_EQ" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Quantità</FormLabel>
              <Input type="number" value={orderQuantity} onChange={e => setOrderQuantity(e.target.value)} />
            </FormControl>
            {/* Limit Price non disponibile al momento per account real money*/}
            {/* <FormControl>
              <FormLabel>[Opzionale] Limit Price ({currency != null ? currency : ""})</FormLabel>
              <Input type="number" value={orderLimitPrice} onChange={e => setOrderLimitPrice(e.target.value)} />
            </FormControl> */}
            <Button type="submit" colorScheme="green" mt={{ base: 4, md: 8 }} minW="120px">Piazza Ordine</Button>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default Trading212Page;